import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  Play,
  Settings,
  Coins,
  Users,
  Keyboard,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Check,
} from 'lucide-react';
import { Reveal } from './Reveal';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface Step {
  title: string;
  description: string;
  tip?: string;
  screenshot: string;
}

interface Chapter {
  id: string;
  icon: React.ElementType;
  label: string;
  num: string;
  title: string;
  subtitle: string;
  steps: Step[];
}

interface ShortcutGroup {
  group: string;
  items: { key: string; desc: string }[];
}

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────

function ShortcutsLayout({ shortcuts }: { shortcuts: ShortcutGroup[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {shortcuts.map((group, gi) => (
        <motion.div
          key={group.group}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: gi * 0.08, duration: 0.4 }}
          className="rounded-2xl border border-[#1414131a] dark:border-[#faf9f51a] bg-[#fffefb] dark:bg-[#1a1918] shadow-warm p-6"
        >
          <p className="mb-5 text-[10px] font-mono uppercase tracking-[0.18em] text-[#ae5630]">
            {group.group}
          </p>
          <div className="space-y-3.5">
            {group.items.map((item) => (
              <div key={item.key} className="flex items-center justify-between gap-4">
                <span className="text-sm text-[#6b6a68] dark:text-[#9a9893]">{item.desc}</span>
                <kbd className="shrink-0 rounded-lg border border-[#1414131a] dark:border-[#faf9f51a] bg-[#f0eee6] dark:bg-[#141413] px-2.5 py-1 text-[11px] font-mono text-[#6b6a68] dark:text-[#9a9893] whitespace-nowrap shadow-inner">
                  {item.key}
                </kbd>
              </div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

interface ScreenshotPanelProps {
  step: Step;
  totalSteps: number;
  activeStep: number;
  animKey: string;
}

function ScreenshotPanel({ step, totalSteps, activeStep, animKey }: ScreenshotPanelProps) {
  return (
    <div className="relative">
      {/* Warm ambient glow */}
      <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-[#ae5630]/5 blur-3xl" />

      <AnimatePresence mode="wait">
        <motion.div
          key={animKey}
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          className="relative"
        >
          {/* Double bezel screenshot wrapper */}
          <div className="rounded-[1.5rem] border border-[#1414131a] dark:border-[#faf9f51a] bg-[#1414130a] dark:bg-[#faf9f50a] p-1.5 shadow-warm-lg">
            <div className="overflow-hidden rounded-[calc(1.5rem-0.375rem)] bg-[#fffefb] dark:bg-[#1a1918] shadow-inner-glow">
              {/* macOS window chrome — warm tones */}
              <div className="flex items-center gap-2 border-b border-[#1414130d] dark:border-[#faf9f50d] px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-[#d98c73]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#d8be8a]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#9fb185]" />
                <span className="ml-3 font-mono text-xs text-[#87867f]">Cherry Agent</span>
              </div>
              <img
                src={step.screenshot}
                alt={step.title}
                className="block h-auto w-full"
                loading="lazy"
              />
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Step progress dots */}
      {totalSteps > 1 && (
        <div className="mt-5 flex items-center justify-center gap-1.5">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ease-spring ${
                i === activeStep
                  ? 'h-1.5 w-5 bg-[#ae5630]'
                  : i < activeStep
                  ? 'h-1.5 w-1.5 bg-[#ae5630]/40'
                  : 'h-1.5 w-1.5 bg-[#1414131a] dark:bg-[#faf9f51a]'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

export default function UserGuide() {
  const { t } = useTranslation();
  const [activeChapter, setActiveChapter] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  const CHAPTERS: Chapter[] = [
    {
      id: 'what',
      icon: BookOpen,
      label: t('userGuide.chapters.what.label'),
      num: t('userGuide.chapters.what.num'),
      title: t('userGuide.chapters.what.title'),
      subtitle: t('userGuide.chapters.what.subtitle'),
      steps: [
        {
          title: t('userGuide.chapters.what.steps.s1.title'),
          description: t('userGuide.chapters.what.steps.s1.description'),
          screenshot: '/guide/guide-main.png',
        },
        {
          title: t('userGuide.chapters.what.steps.s2.title'),
          description: t('userGuide.chapters.what.steps.s2.description'),
          screenshot: '/guide/guide-main.png',
        },
      ],
    },
    {
      id: 'first-task',
      icon: Play,
      label: t('userGuide.chapters.firstTask.label'),
      num: t('userGuide.chapters.firstTask.num'),
      title: t('userGuide.chapters.firstTask.title'),
      subtitle: t('userGuide.chapters.firstTask.subtitle'),
      steps: [
        {
          title: t('userGuide.chapters.firstTask.steps.s1.title'),
          description: t('userGuide.chapters.firstTask.steps.s1.description'),
          screenshot: '/guide/guide-main.png',
        },
        {
          title: t('userGuide.chapters.firstTask.steps.s2.title'),
          description: t('userGuide.chapters.firstTask.steps.s2.description'),
          screenshot: '/guide/guide-main.png',
        },
        {
          title: t('userGuide.chapters.firstTask.steps.s3.title'),
          description: t('userGuide.chapters.firstTask.steps.s3.description'),
          tip: t('userGuide.chapters.firstTask.steps.s3.tip'),
          screenshot: '/guide/guide-new-session.png',
        },
        {
          title: t('userGuide.chapters.firstTask.steps.s4.title'),
          description: t('userGuide.chapters.firstTask.steps.s4.description'),
          screenshot: '/guide/guide-new-session.png',
        },
        {
          title: t('userGuide.chapters.firstTask.steps.s5.title'),
          description: t('userGuide.chapters.firstTask.steps.s5.description'),
          screenshot: '/guide/guide-conversation.png',
        },
      ],
    },
    {
      id: 'settings',
      icon: Settings,
      label: t('userGuide.chapters.settings.label'),
      num: t('userGuide.chapters.settings.num'),
      title: t('userGuide.chapters.settings.title'),
      subtitle: t('userGuide.chapters.settings.subtitle'),
      steps: [
        {
          title: t('userGuide.chapters.settings.steps.s1.title'),
          description: t('userGuide.chapters.settings.steps.s1.description'),
          screenshot: '/guide/guide-settings-memory.png',
        },
        {
          title: t('userGuide.chapters.settings.steps.s2.title'),
          description: t('userGuide.chapters.settings.steps.s2.description'),
          screenshot: '/guide/guide-settings-skills.png',
        },
        {
          title: t('userGuide.chapters.settings.steps.s3.title'),
          description: t('userGuide.chapters.settings.steps.s3.description'),
          screenshot: '/guide/guide-settings-appearance.png',
        },
        {
          title: t('userGuide.chapters.settings.steps.s4.title'),
          description: t('userGuide.chapters.settings.steps.s4.description'),
          screenshot: '/guide/guide-settings-sync.png',
        },
      ],
    },
    {
      id: 'billing',
      icon: Coins,
      label: t('userGuide.chapters.billing.label'),
      num: t('userGuide.chapters.billing.num'),
      title: t('userGuide.chapters.billing.title'),
      subtitle: t('userGuide.chapters.billing.subtitle'),
      steps: [
        {
          title: t('userGuide.chapters.billing.steps.s1.title'),
          description: t('userGuide.chapters.billing.steps.s1.description'),
          screenshot: '/guide/guide-billing-plans.png',
        },
        {
          title: t('userGuide.chapters.billing.steps.s2.title'),
          description: t('userGuide.chapters.billing.steps.s2.description'),
          screenshot: '/guide/guide-checkin.png',
        },
        {
          title: t('userGuide.chapters.billing.steps.s3.title'),
          description: t('userGuide.chapters.billing.steps.s3.description'),
          screenshot: '/guide/guide-billing-plans.png',
        },
        {
          title: t('userGuide.chapters.billing.steps.s4.title'),
          description: t('userGuide.chapters.billing.steps.s4.description'),
          screenshot: '/guide/guide-billing-points.png',
        },
      ],
    },
    {
      id: 'referral',
      icon: Users,
      label: t('userGuide.chapters.referral.label'),
      num: t('userGuide.chapters.referral.num'),
      title: t('userGuide.chapters.referral.title'),
      subtitle: t('userGuide.chapters.referral.subtitle'),
      steps: [
        {
          title: t('userGuide.chapters.referral.steps.s1.title'),
          description: t('userGuide.chapters.referral.steps.s1.description'),
          screenshot: '/guide/guide-referral.png',
        },
        {
          title: t('userGuide.chapters.referral.steps.s2.title'),
          description: t('userGuide.chapters.referral.steps.s2.description'),
          screenshot: '/guide/guide-referral.png',
        },
      ],
    },
    {
      id: 'shortcuts',
      icon: Keyboard,
      label: t('userGuide.chapters.shortcuts.label'),
      num: t('userGuide.chapters.shortcuts.num'),
      title: t('userGuide.chapters.shortcuts.title'),
      subtitle: t('userGuide.chapters.shortcuts.subtitle'),
      steps: [],
    },
  ];

  const SHORTCUTS: ShortcutGroup[] = [
    {
      group: t('userGuide.shortcutGroups.navigation.title'),
      items: [
        { key: '⌘ 1', desc: t('userGuide.shortcutGroups.navigation.items.chat') },
        { key: '⌘ 2', desc: t('userGuide.shortcutGroups.navigation.items.skills') },
        { key: '⌘ 3', desc: t('userGuide.shortcutGroups.navigation.items.memory') },
        { key: '⌘ 4', desc: t('userGuide.shortcutGroups.navigation.items.usage') },
        { key: '⌘ 5', desc: t('userGuide.shortcutGroups.navigation.items.settings') },
      ],
    },
    {
      group: t('userGuide.shortcutGroups.session.title'),
      items: [
        { key: '⌘ N', desc: t('userGuide.shortcutGroups.session.items.new') },
        { key: '⌘ [', desc: t('userGuide.shortcutGroups.session.items.prev') },
        { key: '⌘ ]', desc: t('userGuide.shortcutGroups.session.items.next') },
        { key: '⌘ W', desc: t('userGuide.shortcutGroups.session.items.close') },
      ],
    },
    {
      group: t('userGuide.shortcutGroups.input.title'),
      items: [
        { key: 'Enter', desc: t('userGuide.shortcutGroups.input.items.send') },
        { key: 'Shift + Enter', desc: t('userGuide.shortcutGroups.input.items.newline') },
        { key: '⌘ V', desc: t('userGuide.shortcutGroups.input.items.paste') },
      ],
    },
  ];

  const chapter = CHAPTERS[activeChapter];
  const step = chapter.steps[activeStep];

  const handleChapterChange = (index: number) => {
    setActiveChapter(index);
    setActiveStep(0);
  };

  return (
    <section id="user-guide" className="py-24 md:py-32 bg-[#faf9f5] dark:bg-[#141413]">
      <div className="mx-auto max-w-7xl px-6">

        {/* Section header */}
        <Reveal className="mb-14 text-center">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-4 py-1.5 text-[#6b6a68] dark:text-[#9a9893]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
            {t('userGuide.sectionLabel')}
          </span>
          <h2 className="font-display display-tight mt-5 text-3xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5] md:text-5xl">
            {t('userGuide.title')}
          </h2>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
            {t('userGuide.subtitle')}
          </p>
        </Reveal>

        {/* Chapter tab bar — warm pill switcher */}
        <Reveal className="mb-10 flex justify-center">
          <div className="relative flex overflow-x-auto max-w-full rounded-2xl border border-[#1414131a] dark:border-[#faf9f51a] bg-[#f0eee6] dark:bg-[#1a1918] p-1">
            {CHAPTERS.map((ch, i) => {
              const Icon = ch.icon;
              const isActive = activeChapter === i;
              return (
                <button
                  key={ch.id}
                  onClick={() => handleChapterChange(i)}
                  className="relative z-10 flex items-center gap-1.5 whitespace-nowrap rounded-xl px-3.5 py-2 text-sm font-medium transition-colors duration-200"
                >
                  {isActive && (
                    <motion.div
                      layoutId="chapter-tab-pill"
                      className="absolute inset-0 rounded-xl bg-[#ae5630] shadow-warm-sm"
                      transition={{ type: 'spring', bounce: 0.18, duration: 0.5 }}
                    />
                  )}
                  <Icon
                    size={13}
                    className={`relative z-10 transition-colors duration-200 ${
                      isActive ? 'text-white' : 'text-[#87867f] dark:text-[#87867f]'
                    }`}
                  />
                  <span
                    className={`relative z-10 transition-colors duration-200 ${
                      isActive
                        ? 'font-semibold text-white'
                        : 'text-[#6b6a68] dark:text-[#9a9893]'
                    }`}
                  >
                    {ch.label}
                  </span>
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* Chapter content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeChapter}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
          >
            {/* Chapter header with decorative number */}
            <div className="relative mb-10 pl-1">
              <span
                className="pointer-events-none absolute -top-3 -left-2 select-none font-display text-[80px] font-medium leading-none text-[#1414130d] dark:text-[#faf9f50d] md:text-[100px]"
              >
                {chapter.num}
              </span>
              <div className="relative">
                <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#ae5630]">
                  {chapter.label}
                </p>
                <h3 className="font-display text-xl font-medium text-[#141413] dark:text-[#faf9f5] md:text-2xl">
                  {chapter.title}
                </h3>
                <p className="mt-1.5 text-sm text-[#6b6a68] dark:text-[#9a9893]">{chapter.subtitle}</p>
              </div>
            </div>

            {/* Shortcuts: special layout */}
            {chapter.id === 'shortcuts' ? (
              <ShortcutsLayout shortcuts={SHORTCUTS} />
            ) : (
              <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-5 lg:gap-12">

                {/* Left: step timeline */}
                <div className="lg:col-span-2">
                  <div className="relative">
                    {/* Vertical connector */}
                    {chapter.steps.length > 1 && (
                      <div className="absolute left-3 top-5 bottom-5 w-px bg-gradient-to-b from-[#ae5630]/30 via-[#ae5630]/15 to-transparent" />
                    )}

                    <div className="space-y-1">
                      {chapter.steps.map((s, i) => {
                        const isActive = activeStep === i;
                        const isDone = i < activeStep;
                        return (
                          <button
                            key={i}
                            onClick={() => setActiveStep(i)}
                            className="group relative w-full text-left"
                          >
                            <div className="flex items-start gap-4 py-1.5">
                              {/* Circle badge */}
                              <span
                                className={`relative z-10 mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-bold transition-all duration-300 ease-spring ${
                                  isActive
                                    ? 'border-[#ae5630] bg-[#ae5630] text-white shadow-warm-sm'
                                    : isDone
                                    ? 'border-[#ae5630]/50 bg-[#ae5630]/15 text-[#ae5630]'
                                    : 'border-[#1414131a] dark:border-[#faf9f51a] bg-[#fffefb] dark:bg-[#1a1918] text-[#87867f] group-hover:border-[#ae5630]/40'
                                }`}
                              >
                                {isDone ? <Check size={10} /> : i + 1}
                              </span>

                              {/* Content card */}
                              <div
                                className={`min-w-0 flex-1 rounded-xl border p-3.5 transition-all duration-300 ease-spring ${
                                  isActive
                                    ? 'border-[#ae5630]/30 bg-[#fffefb] dark:bg-[#1a1918] shadow-warm'
                                    : 'border-transparent bg-[#f0eee6]/60 dark:bg-[#1a1918]/50 group-hover:border-[#1414131a] dark:group-hover:border-[#faf9f51a] group-hover:bg-[#f0eee6] dark:group-hover:bg-[#1a1918]'
                                }`}
                              >
                                <p
                                  className={`text-sm font-medium leading-snug transition-colors duration-200 ${
                                    isActive
                                      ? 'text-[#141413] dark:text-[#faf9f5]'
                                      : isDone
                                      ? 'text-[#87867f] line-through decoration-[#1414131a]'
                                      : 'text-[#6b6a68] dark:text-[#9a9893] group-hover:text-[#141413] dark:group-hover:text-[#faf9f5]'
                                  }`}
                                >
                                  {s.title}
                                </p>

                                {/* Expanded content */}
                                {isActive && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
                                    className="overflow-hidden"
                                  >
                                    <p className="mt-2.5 whitespace-pre-line text-sm leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
                                      {s.description}
                                    </p>

                                    {/* Tip callout */}
                                    {s.tip && (
                                      <div className="mt-3.5 flex gap-2.5 rounded-xl border border-[#ae5630]/20 bg-[#ae5630]/8 p-3">
                                        <AlertCircle
                                          size={13}
                                          className="mt-0.5 shrink-0 text-[#ae5630]"
                                        />
                                        <p className="text-xs leading-relaxed text-[#ae5630]/90">
                                          {s.tip}
                                        </p>
                                      </div>
                                    )}

                                    {/* Next step CTA */}
                                    {activeStep < chapter.steps.length - 1 && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveStep(activeStep + 1);
                                        }}
                                        className="mt-4 flex items-center gap-1 text-xs text-[#87867f] transition-colors duration-200 hover:text-[#ae5630]"
                                      >
                                        {t('userGuide.nextStep')}
                                        <ChevronRight size={12} />
                                      </button>
                                    )}
                                  </motion.div>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Right: screenshot */}
                {step && (
                  <div className="lg:col-span-3">
                    <ScreenshotPanel
                      step={step}
                      totalSteps={chapter.steps.length}
                      activeStep={activeStep}
                      animKey={step.screenshot}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Chapter prev / next navigation */}
            <div className="mt-10 flex items-center justify-between border-t border-[#1414131a] dark:border-[#faf9f51a] pt-6">
              <button
                onClick={() => handleChapterChange(activeChapter - 1)}
                className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-300 ease-spring ${
                  activeChapter === 0
                    ? 'invisible'
                    : 'text-[#6b6a68] dark:text-[#9a9893] hover:bg-[#f0eee6] dark:hover:bg-[#1a1918] hover:text-[#141413] dark:hover:text-[#faf9f5]'
                }`}
              >
                <ChevronLeft size={15} />
                {activeChapter > 0 && CHAPTERS[activeChapter - 1].label}
              </button>

              {/* Chapter dots */}
              <div className="flex items-center gap-1.5">
                {CHAPTERS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handleChapterChange(i)}
                    className={`rounded-full transition-all duration-300 ease-spring ${
                      i === activeChapter
                        ? 'h-1.5 w-5 bg-[#ae5630]'
                        : 'h-1.5 w-1.5 bg-[#1414131a] dark:bg-[#faf9f51a] hover:bg-[#ae5630]/40'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={() => handleChapterChange(activeChapter + 1)}
                className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-300 ease-spring ${
                  activeChapter === CHAPTERS.length - 1
                    ? 'invisible'
                    : 'text-[#6b6a68] dark:text-[#9a9893] hover:bg-[#f0eee6] dark:hover:bg-[#1a1918] hover:text-[#141413] dark:hover:text-[#faf9f5]'
                }`}
              >
                {activeChapter < CHAPTERS.length - 1 && CHAPTERS[activeChapter + 1].label}
                <ChevronRight size={15} />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
